import * as batchStatuses from "../../constants/batchStatuses";
import * as formulaErrors from "../../constants/formulaErrors";
import * as rcc from "../../constants/rcc";

/**
 * @class FormulaValidation
 */
export default class FormulaValidation {
    /**
     * @param {Array}   validationErrors
     * @param {Number}  machineValidationAll
     * @param {Boolean} totalCntAmounts
     * @param {Boolean} overfill
     * @param {Boolean} minDosableAmount
     * @param {Boolean} batchNumbers
     * @param {Boolean} colorantBaseRules
     * @param {Boolean} availableColorantAmounts    Checks if there is enough space in every canister for sum of colorant amounts
     */
    constructor(
        validationErrors,
        machineValidationAll,
        totalCntAmounts,
        overfill,
        minDosableAmount,
        batchNumbers,
        colorantBaseRules,
        availableColorantAmounts
    ) {
        this._validationErrors = validationErrors;
        this._machineValidationAll = machineValidationAll;
        this._totalCntAmounts = totalCntAmounts;
        this._overfill = overfill;
        this._minDosableAmount = minDosableAmount;
        this._batchNumbers = batchNumbers;
        this._colorantBaseRules = colorantBaseRules;
        this._availableColorantAmounts = availableColorantAmounts;
        this._errors = [];
        this._hasColorantErrors = false;
        this._colorantErrors = [];
        this._errorsByColorant = {};
        this._hasBatchNumberErrors = false;
        this._batchNumberErrors = {};
        this._batchNumberMainError = null;
        this._hasCriticalLevelForAllPackages = false;
        this._invalid = false;

        this.processErrors();
    }

    /**
     * @return {Array}
     */
    get validationErrors() {
        return this._validationErrors;
    }

    /**
     * @return {Number}
     */
    get machineValidationAll() {
        return this._machineValidationAll;
    }

    /**
     * @return {Array}
     */
    get errors() {
        return this._errors;
    }

    processErrors = () => {
        let errors = [];
        /* eslint-disable-next-line no-undef */
        let colorantErrors = new Set();
        let errorsByColorant = {};
        let batchNumberErrors = {};

        if (Array.isArray(this._validationErrors)) {
            for (let error of this._validationErrors) {
                switch (error.id) {
                    case formulaErrors.TOTAL_COLORANT_AMOUNT_BELLOW_MIN_AMOUNT:
                    case formulaErrors.TOTAL_COLORANT_AMOUNT_OVER_MAX_AMOUNT:
                        if (this._totalCntAmounts) {
                            errors.push(error.id);
                        }
                        break;
                    case formulaErrors.OVERFILL_DETECTED:
                        if (this._overfill) {
                            errors.push(error.id);
                        }
                        break;
                    case formulaErrors.COLORANT_AMOUNT_BELLOW_MIN_DOSABLE_AMOUNT:
                        if (this._minDosableAmount && error.colorant !== null) {
                            if (errorsByColorant[error.colorant.id] === undefined) {
                                errorsByColorant[error.colorant.id] = [];
                            }

                            colorantErrors.add(error.id);
                            errorsByColorant[error.colorant.id].push(error.id);
                        }
                        break;
                    case formulaErrors.COLORANT_BATCH_STATUS_WARNING:
                        if (this._batchNumbers) {
                            if (!this._batchNumberMainError) {
                                this._batchNumberMainError = formulaErrors.COLORANT_BATCH_STATUS_INVALID;
                            }
                            batchNumberErrors[error.colorant.id] = {
                                name: error.colorant.displayName,
                                status: batchStatuses.BATCH_STATUS_WARNING,
                                batchNumber: error.value
                            };
                        }
                        break;
                    case formulaErrors.COLORANT_BATCH_STATUS_INVALID:
                        if (this._batchNumbers) {
                            this._batchNumberMainError = formulaErrors.COLORANT_BATCH_STATUS_INVALID;
                            batchNumberErrors[error.colorant.id] = {
                                name: error.colorant.displayName,
                                status: batchStatuses.BATCH_STATUS_INVALID,
                                batchNumber: error.value
                            };
                        }
                        break;
                    case formulaErrors.COLORANT_BASE_RULES_BROKEN:
                        if (this._colorantBaseRules && error.colorant !== null) {
                            if (errorsByColorant[error.colorant.id] === undefined) {
                                errorsByColorant[error.colorant.id] = [];
                            }

                            colorantErrors.add(error.id);
                            errorsByColorant[error.colorant.id].push(error.id);
                        }
                        break;
                    default:
                        break;
                }
            }
        }

        this._errors = errors;
        this._hasColorantErrors = colorantErrors.size > 0;
        this._colorantErrors = [...colorantErrors];
        this._errorsByColorant = errorsByColorant;
        this._hasBatchNumberErrors = Object.keys(batchNumberErrors).length > 0;
        this._batchNumberErrors = batchNumberErrors;
        this._hasCriticalLevelForAllPackages =
            this._availableColorantAmounts && this._machineValidationAll === rcc.RCC_VALIDATION_CRITICAL_LEVEL;
        this._invalid =
            errors.length > 0 ||
            this._hasColorantErrors ||
            this._hasBatchNumberErrors ||
            this._hasCriticalLevelForAllPackages;
    };

    /**
     * @return {Boolean}
     */
    isInvalid = () => {
        return this._invalid;
    };

    /**
     * @return {Boolean}
     */
    hasNoErrors = () => {
        return this._errors.length === 0;
    };

    /**
     * @return {Boolean}
     */
    hasErrors = () => {
        return this._errors.length > 0;
    };

    /**
     * @param   {Number}    errorId
     * @return  {Boolean}
     */
    hasError = (errorId) => {
        return this._errors.includes(errorId);
    };

    /**
     * @return {Array}
     */
    getMainErrors = () => {
        if (this.hasColorantErrors()) {
            return this._errors.concat(this._colorantErrors);
        }

        return this._errors;
    };

    /**
     * @return {Boolean}
     */
    isFormulaInvalid = () => {
        return this.hasErrors() || this._hasColorantErrors;
    };

    /**
     * @return {Boolean}
     */
    hasColorantErrors = () => {
        return this._hasColorantErrors;
    };

    /**
     * @param   {Number}    colorantId
     * @return  {Boolean}
     */
    hasErrorByColorant = (colorantId) => {
        if (this._hasColorantErrors) {
            return this._errorsByColorant[colorantId] !== undefined;
        }

        return false;
    };

    /**
     * @param   {Number}    colorantId
     * @return  {Array}
     */
    getErrorsByColorant = (colorantId) => {
        if (this._hasColorantErrors) {
            return this._errorsByColorant[colorantId] === undefined ? [] : this._errorsByColorant[colorantId];
        }

        return [];
    };

    hasColorantBaseRulesError = () => {
        return this._validationErrors.some((error) => error.id === formulaErrors.COLORANT_BASE_RULES_BROKEN);
    };

    /**
     * @return {Boolean}
     */
    isBatchNumberInvalid = () => {
        return this._hasBatchNumberErrors;
    };

    getBatchNumberErrors = () => {
        let result = [];
        if (this._hasBatchNumberErrors) {
            for (let colorantId in this._batchNumberErrors) {
                result.push(this._batchNumberErrors[colorantId]);
            }
        }
        if (result.length > 0) return result;

        return null;
    };

    /**
     * @return {Number | null}
     */
    getBatchNumberError = () => {
        if (this.isBatchNumberInvalid()) {
            let resultInvalid = [];
            let resultWarning = [];
            if (this._hasBatchNumberErrors) {
                for (let colorantId in this._batchNumberErrors) {
                    if (this._batchNumberErrors[colorantId].status === batchStatuses.BATCH_STATUS_INVALID) {
                        resultInvalid.push(this._batchNumberErrors[colorantId].name);
                    } else {
                        resultWarning.push(this._batchNumberErrors[colorantId].name);
                    }
                }
            }
            return {
                invalid: resultInvalid.join(", "),
                warning: resultWarning.join(", ")
            };
        }

        return null;
    };

    /**
     * Canisters will reach critical levels to dispense formula colorant amounts for all packages.
     *
     * @return {Boolean}
     */
    hasCriticalLevelForAllPackages = () => {
        return this._hasCriticalLevelForAllPackages;
    };

    /**
     * @return {Object}
     */
    reset() {
        return new FormulaValidation(
            [],
            rcc.RCC_VALIDATION_OK,
            this._totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    }

    /**
     * @param   {Array}     validationErrors
     * @return  {Object}
     */
    updateValidationErrors = (validationErrors) => {
        return new FormulaValidation(
            validationErrors,
            this._machineValidationAll,
            this._totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Number}    machineValidationAll
     * @return  {Object}
     */
    updateMachineValidationAll = (machineValidationAll) => {
        return new FormulaValidation(
            this._validationErrors,
            machineValidationAll,
            this._totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Boolean}   totalCntAmounts
     * @return  {Object}
     */
    updateTotalCntAmounts = (totalCntAmounts) => {
        return new FormulaValidation(
            this._validationErrors,
            this._machineValidationAll,
            totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Boolean}   overfill
     * @return  {Object}
     */
    updateOverfill = (overfill) => {
        return new FormulaValidation(
            this._validationErrors,
            this._machineValidationAll,
            this._totalCntAmounts,
            overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Boolean}   minDosableAmount
     * @return  {Object}
     */
    updateMinDosableAmount = (minDosableAmount) => {
        return new FormulaValidation(
            this._validationErrors,
            this._machineValidationAll,
            this._totalCntAmounts,
            this._overfill,
            minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Boolean}   colorantBaseRules
     * @return  {Object}
     */
    updateColorantBaseRules = (colorantBaseRules) => {
        return new FormulaValidation(
            this._validationErrors,
            this._machineValidationAll,
            this._totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            colorantBaseRules,
            this._availableColorantAmounts
        );
    };

    /**
     * @param   {Boolean}   availableColorantAmounts
     * @return  {Object}
     */
    updateAvailableColorantAmounts = (availableColorantAmounts) => {
        return new FormulaValidation(
            this._validationErrors,
            this._machineValidationAll,
            this._totalCntAmounts,
            this._overfill,
            this._minDosableAmount,
            this._batchNumbers,
            this._colorantBaseRules,
            availableColorantAmounts
        );
    };
}
